import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux'

import useAuthentication from '../hooks/useAuthentication';
import useBrowserTabTitle from '../hooks/useBrowserTabTitle';

import { setProject } from '../obj/pscState';

const ResetPassword = () => {
    useBrowserTabTitle('Reset Password')

    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const dispatch = useDispatch()

    const {
        currentUser,
        updateUser,
    } = useAuthentication();

    const [username, setUsername] = useState('');
    const [timestamp, setTimestamp] = useState('');
    const [checksum, setChecksum] = useState('')
    const [hasParameters, setHasParameters] = useState(false)

    const [password, setPassword] = useState('')
    const [password2, setPassword2] = useState('')
    const [verificationCode, setVerificationCode] = useState('')
    const [submitActive, setSubmitActive] = useState(false)
    const [linkExpired, setLinkExpired] = useState(false)
    const [error, setError] = useState(false)
    const [isReady, setIsReady] = useState(false)
    const [is2faConfigured, setIs2faConfigured] = useState(false)

    useEffect(() => {
        if( currentUser.id > 0 ) {
            return navigate('/main')
        }
    }, [navigate, currentUser])

    useEffect(() => {
        let username = searchParams.get('u')
        let timestamp = searchParams.get('t')
        let checksum = searchParams.get('c')

        if ( ! username || ! timestamp || ! checksum ) {
            return
        }

        setUsername(username)
        setTimestamp(timestamp)
        setChecksum(checksum)
        setHasParameters(true)

        fetch('/v0/test_reset_password', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                username: username,
                timestamp: timestamp,
                checksum: checksum
            })
        })
        .then(response => response.json())
        .then(res => {
            setIsReady(true)
            setIs2faConfigured(res.is_2fa_configured)
        })
        .catch(() => {
            setIsReady(true)
            setLinkExpired(true)
        })
    }, [])

    const handleSubmit = (event) => {
        event.preventDefault()

        if( is2faConfigured && verificationCode === '' ) {
            setError('Verification Code is required')
            return
        }

        if( password !== password2 ) {
            setError('The two new passwords do not match')
            return
        }

        if( password.length < 10 ) {
            setError('Your new password must be at least 10 characters')
            return
        }

        setSubmitActive(true)

        fetch('/v0/reset_password', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                username: username,
                timestamp: timestamp,
                checksum: checksum,
                password: password,
                verification_code: verificationCode
            })
        })
        .then(response => response.json())
        .then(res => {
            updateUser(res.result);

            if( res.result.current_project_id > 0 ) {
                fetch('/v0/projects/' + res.result.current_project_id, { credentials: 'same-origin' })
                    .then(function(response) {
                        return response.json()
                    })
                    .then(function(json) {
                        dispatch(setProject(json.result))
                    })
            }
        })
        .catch(err => {
            setSubmitActive(false)
            let msg = err.json()
            if( msg.error === 'EBADTOKEN' ) {
                msg.error = 'Invalid Verification Code';
            } else {
                msg.error = 'An error occured. Please try again later.'
            }
            setError(msg.error)
        })
    }

    if ( linkExpired ) {
        return (
            <div className="wrapper wrapper-content">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="text-center m-t-lg">
                            <h1>
                                Link has expired
                            </h1>
                            <h1>
                            </h1>
                            <p>
                                If you still need to reset your password, 
                                go to <Link to="/forgot_password" >this page</Link>.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if ( ! hasParameters ) {
        return (
            <div className="wrapper wrapper-content">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="text-center m-t-lg">
                            <h1>
                                Invalid parameters
                            </h1>
                            <h1>
                            </h1>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if ( ! isReady ) {
        return (
            <div />
        )
    }

    const require2fa = is2faConfigured ? (
        <div>
            <div className="form-group">
                As your account is configured to use Two Factor Authentication, please enter your Verification Code.
            </div>

            <div className="form-group"><label>Verification Code</label>
                <input type="text" placeholder="Verification Code" autoComplete="false" className="form-control" onChange={(e) => setVerificationCode(e.target.value)}/>
            </div>
        </div>
    ) : (
        <div />
    )

    let requiredIndicator = (<label className="form-required-star">&nbsp;*</label>);

    return (

        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="text-center m-t-lg">
                        <h1>
                            Reset your password
                        </h1>
                        <h1>
                        </h1>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-lg-3">
                </div>
                <div className="col-lg-6">
                    <div className="ibox float-e-margins">
                        <div className="ibox-content">
                            <div className="row">
                                <div className="col-sm-12">
                                    <h3 className="m-t-none m-b">Enter your new password</h3>
                                    {error && <div className="alert alert-danger">{error}</div>}
                                    <form role="form" autoComplete="off" onSubmit={handleSubmit}>

                                        <div className="form-group">
                                            Your new password must be at least 10 characters long.
                                        </div>

                                        <div className="form-group"><label>New Password
                                            {requiredIndicator}
                                        </label> <input type="password" placeholder="Password" autoComplete="off" className="form-control" onChange={(e) => setPassword(e.target.value)}/></div>
                                        <div className="form-group"><label>New Password (again)
                                            {requiredIndicator}
                                        </label> <input type="password" placeholder="Password" autoComplete="off" className="form-control" onChange={(e) => setPassword2(e.target.value)}/></div>

                                        {require2fa}

                                        <div className="form-group">

                                            <button className={'btn btn-sm btn-primary pull-right m-t-n-xs has-spinner ' + (submitActive ? 'active' : '')} type="submit">
                                                <span className="psc-spinner"><i className="fa fa-refresh fa-spin fa-fw"></i></span>
                                                Reset Password
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-3">
                </div>
            </div>
        </div>
    )
}

export default ResetPassword
